import RegularLanguageSelect from '@/components/header/language-select/RegularLanguageSelect';
import Logo from '@/components/header/Logo';
import TenantSelect from '@/components/header/nav-menu/TenantSelect';
import { Icons } from '@/components/icons';
import { Button } from '@/components/primitives/Button';
import Divider from '@/components/primitives/Divider';
import { Link } from '@/components/primitives/Link';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from '@/components/primitives/NavigationMenu';
import {
  PopoverContent,
  PopoverItem,
  PopoverRoot,
  PopoverTrigger,
} from '@/components/primitives/Popover';
import { Typography } from '@/components/primitives/Typography';
import { tenantSelector } from '@/features/configuration/configurationSelector';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import { useAuth } from '@/hooks/useAuth';
import { TransText, type TransTextKeys } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type { NavigationItem } from '@/types/navigation';
import { isLocalDevelopment } from '@/utils/isLocalDev';
import { getDisplayName } from '@/utils/profile';
import { getTenantConfiguration } from '@/utils/tenant';
import type { FC, ReactNode } from 'react';
import { Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type RegularNavMenuProps = {
  menuItems: Array<NavigationItem>;
  userMenuItems?: Array<[TransTextKeys, string, ReactNode]>;
};

const RegularNavMenu: FC<RegularNavMenuProps> = ({
  menuItems,
  userMenuItems,
}) => {
  const { isAuthenticated, logout } = useAuth();
  const profile = useSelector(profileDataSelector);
  const displayName = getDisplayName(profile);
  const location = useLocation();
  const navigate = useNavigate();
  const selectedTenant = useSelector(tenantSelector);
  const configuration = getTenantConfiguration(selectedTenant.value);

  return (
    <div
      className="mx-auto flex w-full items-center justify-between"
      data-testid="navbar-regular"
    >
      <div className="flex items-center gap-6">
        <Logo />
        <nav className="flex items-center">
          <NavigationMenu>
            <NavigationMenuList className="flex items-center gap-4">
              {menuItems.map((item, index) => {
                const isActive = location.pathname.includes(item.path);

                return (
                  <Fragment key={item.path}>
                    <NavigationMenuItem>
                      <Link
                        to={isActive ? location.pathname : item.path}
                        active={isActive}
                      >
                        {item.name}
                      </Link>
                    </NavigationMenuItem>
                    {index < menuItems.length - 1 && (
                      <Divider
                        orientation="vertical"
                        className="h-4"
                        variant="strong"
                      />
                    )}
                  </Fragment>
                );
              })}
            </NavigationMenuList>
          </NavigationMenu>
        </nav>
      </div>
      <div className="flex items-center gap-4">
        {isLocalDevelopment() && (
          <>
            <TenantSelect />
            <Divider orientation="vertical" className="h-4" variant="strong" />
          </>
        )}
        {isAuthenticated ? (
          <PopoverRoot>
            <PopoverTrigger asChild>
              <Button
                data-testid="my-account-button"
                variant="ghostLink"
                className="group flex gap-2"
              >
                <Icons.user />
                {displayName}
                <Icons.chevronDown
                  height={16}
                  width={16}
                  className="transition-transform duration-200 group-data-[state=open]:rotate-180"
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-48 overflow-hidden rounded-md p-0"
              align="end"
            >
              {userMenuItems?.map(([labelKey, path, icon], key) => {
                const isActive =
                  path !== 'logout' && location.pathname.includes(path);

                return (
                  <Fragment key={key}>
                    {path === 'logout' && <Divider variant="regular" />}
                    <PopoverItem
                      active={isActive}
                      onClick={() =>
                        path === 'logout' ? logout() : navigate(path)
                      }
                      data-testid={`user-menu-${labelKey}`}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <span className="mr-2 text-primary">{icon}</span>
                        <Typography variant="button">
                          <TransText i18nKey={labelKey} />
                        </Typography>
                      </div>
                      {isActive && (
                        <Icons.checkCircle
                          height={16}
                          width={16}
                          className="text-success-text"
                        />
                      )}
                    </PopoverItem>
                  </Fragment>
                );
              })}
            </PopoverContent>
          </PopoverRoot>
        ) : (
          configuration?.showLogin && (
            <>
              <Link
                to="/login"
                data-testid="my-account-button"
                className="flex gap-2"
                active={location.pathname === '/login'}
              >
                <Icons.user />
                <TransText i18nKey="logIn" />
              </Link>
              <Divider
                orientation="vertical"
                className="h-4"
                variant="strong"
              />
            </>
          )
        )}
        <RegularLanguageSelect />
      </div>
    </div>
  );
};

export default RegularNavMenu;
